/** @format */

import React, { ChangeEvent, FunctionComponent, useState } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { ComponentTypeParam } from '../../../interfaces/collections/SimCardServiceInterface';
import { SimpleSelect, SimpleSelectElements } from '../../../components/Adescom/SimpleSelect';
import { FeaturesAction } from '../../../modules/features/reducers';
import { FormControl, FormControlLabel, FormLabel, Input, InputLabel, Radio, RadioGroup } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import CustomInputExt from '../../../components/CustomInput/CustomInputExt';
import CustomInputExtNumber from '../../../components/CustomInput/CustomInputExtNumber';
import { Moment } from 'moment';
import Datetime from 'react-datetime';
import I18n from '../../../components/Translate/I18n';

type ComponentApiParamType = {
    params: ComponentTypeParam;
    name: string;
    textCallback(fieldName: string, value: string): void;
    extraFieldName?: string;
};

export const ComponentApiParam: FunctionComponent<ComponentApiParamType> = ({ params, name, textCallback, extraFieldName }): React.ReactElement => {
    const dispatch = useDispatch();
    const [radioGroupValue, setRadioGroupValue] = useState<boolean | null>(typeof params.default === 'boolean' ? params.default : null);
    const [inputString, setInputString] = useState('');
    let isNumeric = false;
    const getComponent = (params: ComponentTypeParam): React.ReactElement => {
        if (params.input === 'select') {
            return prepareSelect(params);
        }
        if (params.input === 'radio') {
            return prepareRadio(params);
        }
        if (params.input === 'readonly') {
            return prepareReadOnly(params);
        }
        if (params.input === 'string') {
            return prepareInputString(params);
        }
        if (params.input === 'numeric') {
            isNumeric = true;
            return prepareInputString(params);
        }
        if (params.input === 'date') {
            return prepareInputDate(params);
        }
        return <div />;
    };

    const prepareReadOnly = (params: ComponentTypeParam): React.ReactElement => {
        return (
            <React.Fragment>
                <hr />
                <FormControl component="fieldset">
                    <InputLabel>{name}</InputLabel>
                    <Input readOnly={true} value={params.default} />
                </FormControl>
            </React.Fragment>
        );
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        const checked = event.target.checked;
        dispatch({
            type: FeaturesAction.FEATURES_ACTIVATE_WITH_PARAMETERS,
            selectedValue: value === 'true',
            valueChange: checked,
            fieldName: name,
        });
        setRadioGroupValue(value === 'true');
    };

    const prepareRadio = (params: ComponentTypeParam): React.ReactElement => {
        return (
            <React.Fragment>
                <hr />
                <FormControl component="fieldset">
                    <FormLabel component="legend">{name}</FormLabel>
                    <RadioGroup aria-label={params.param_name} name={params.param_name} value={radioGroupValue} onChange={handleChange}>
                        {params.allowedValue.map((item) => {
                            let nameRadio = 'true';
                            if (!item.value) {
                                nameRadio = 'false';
                            }
                            return <FormControlLabel key={nameRadio + params.param_name} control={<Radio />} label={nameRadio} value={item.value} />;
                        })}
                    </RadioGroup>
                </FormControl>
            </React.Fragment>
        );
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        setInputString(value);
        textCallback(field, value);
    };

    const prepareInputDate = (params: ComponentTypeParam): React.ReactElement => {
        return (
            <React.Fragment>
                <div style={{ marginTop: '15px' }}>
                    <I18n trans="simcard:f.param_value_label" valueObject={{ paramName: params.param_name }} />
                </div>
                <Datetime
                    locale="pl"
                    viewMode="days"
                    /** defaultValue={moment(DateHelperTs.getCurrentDateSimple('YYYY-MM-DD'))} **/
                    dateFormat="YYYY-MM-DD"
                    timeFormat={false}
                    onChange={(moment: Moment | string): void => {
                        if (typeof moment !== 'string') {
                            textCallback(params.param_name, moment.format('YYYY-MM-DD 23:59:59'));
                        }
                    }}
                    inputProps={{
                        id: params.param_name,
                        placeholder: 'YYYY-MM-DD',
                    }}
                />
            </React.Fragment>
        );
    };

    const prepareInputString = (params: ComponentTypeParam): React.ReactElement => {
        return (
            <React.Fragment>
                {!isNumeric && (
                    <CustomInputExt
                        formControlProps={{ fullWidth: true }}
                        required={params.required}
                        labelText="simcard:f.param_value_label"
                        labelTextValue={{ paramName: params.param_name }}
                        name={params.param_name}
                        value={inputString}
                        onChange={onChangeEvent}
                    />
                )}
                {isNumeric && (
                    <CustomInputExtNumber
                        formControlProps={{ fullWidth: true }}
                        required={params.required}
                        labelText="simcard:f.param_value_label"
                        labelTextValue={{ paramName: params.param_name }}
                        name={params.param_name}
                        value={inputString}
                        onChange={onChangeEvent}
                    />
                )}
            </React.Fragment>
        );
    };

    const prepareSelect = (params: ComponentTypeParam): React.ReactElement => {
        const element: Array<SimpleSelectElements> = [];
        let selectedDefault: string | number = params.default && typeof params.default !== 'boolean' ? params.default : -1;
        let value: string | number | boolean | null = null;
        for (let i = 0; i < params.allowedValue.length; i++) {
            value = params.allowedValue[i].value;
            if (typeof value === 'string' || typeof value === 'number') {
                element.push({ value: value, name: typeof value === 'number' ? value.toString() : value });
            }
        }
        return (
            <SimpleSelect
                selectedValue={selectedDefault}
                fieldName={params.param_name}
                label={name}
                required={params.required}
                elements={element}
                dispatchName={FeaturesAction.FEATURES_ACTIVATE_WITH_PARAMETERS}
                extraFieldName={extraFieldName}
            />
        );
    };

    return (
        <GridContainer>
            <GridItem xs={12}>{getComponent(params)}</GridItem>
        </GridContainer>
    );
};
